import { DataTable, PngImage } from '@ecosystem/export-api';
import {
  AccessTags,
  ChartTags,
  ConfigItem,
  ConfigItemValue,
  CreateViewStateFn,
  DirectLinkMeta,
  MarketTags,
  PageTags,
  PageView,
} from '@ecosystem/newron-client-lib';

export enum CollectionsHeaderKey {
  Name = 'name',
  LastViewedAt = 'lastViewedAt',
  Client = 'client',
  LastChanged = 'lastChangedAt',
  OwnerDisplayName = 'ownerDisplayName',
  QuickActions = 'quickActions',
  Sharing = 'sharing',
}

export enum TemplatesHeaderKey {
  Name = 'name',
  ProductGroups = 'productGroups',
  Countries = 'countries',
  Periodicity = 'periodicity',
  LastChanged = 'lastChangedAt',
}

export enum AccessLevel {
  Owner = 'owner',
  Viewer = 'viewer',
}

export enum Interaction {
  EditCollection = 'EditCollection',
  DuplicateCollection = 'DuplicateCollection',
  CreateTemplate = 'CreateTemplate',
  DeleteCollection = 'DeleteCollection',
  RemovePermissions = 'RemovePermissions',
  ExportCollection = 'ExportCollection',
  EditInsight = 'EditInsight',
  DeleteInsight = 'DeleteInsight',
  ExportInsight = 'ExportInsight',
  CopyInsight = 'CopyInsight',
  ShareCollection = 'ShareCollection',
  AssignClientToCollection = 'AssignClientToCollection',
  AddTextBlock = 'AddTextBlock',
  EditInsightConfigItems = 'EditInsightConfigItems',
  MultiEdit = 'MultiEdit',
}

export type InteractionState = 'enabled' | 'disabled' | 'hidden';

export enum InteractionElement {
  overviewMenu = 'overviewMenu',
  detailsButton = 'detailsButton',
  detailsMenu = 'detailsMenu',
}

export type AvailableInteractions = {
  [key in Interaction]: Partial<Record<InteractionElement, InteractionState>>;
};

export interface CollectionInsight<TInsightData> {
  id: string;
  name: string;
  description?: string;
  createdAt: Date;
  dataType: CollectionDataType;
  proposition: Proposition;
  type: string;
  data: TInsightData;
  viewKey?: string | null;
  position?: number;
}

export interface UpdatedInsight<TInsightData> extends CollectionInsight<TInsightData> {
  hasPropositionError?: boolean;
}

export enum MarketTagType {
  Countries = 'country_code',
  ProductGroups = 'item_group_code',
}

export enum CollectionTagKeys {
  Countries = 'Countries',
  ProductGroups = 'Product groups',
  Periodicity = 'Periodicity',
}

export type CollectionTag = {
  key: CollectionTagKeys;
  values: string[];
};

export type CollectionTagsRecord = Record<CollectionTagKeys, string[]>;

export interface Collection<TInsightData> {
  id: string;
  type: CollectionType;
  name: string;
  createdAt: Date;
  insights: CollectionInsight<TInsightData>[];
  description?: string | null;
  accessLevel?: AccessLevel;
  sharedWithCount?: number;
  sharedWithUserGroupsCount?: number;
  owner?: UserInfo;
  assignedClients?: Client[];
  tags?: CollectionTag[];
}

export type CollectionToEdit = Pick<
  Collection<unknown>,
  'id' | 'name' | 'description' | 'type' | 'tags' | 'sharedWithCount' | 'assignedClients' | 'accessLevel' | 'owner'
>;
export type InsightToEdit = Pick<CollectionInsight<unknown>, 'id' | 'name' | 'description'>;
export type InsightData = Pick<CollectionInsight<unknown>, 'id' | 'data' | 'viewKey' | 'dataType'>;

export interface CollectionListItem {
  id: string;
  type: CollectionType;
  name: string;
  createdAt: Date;
  insightsCount: number;
  description?: string | null;
  accessLevel?: AccessLevel;
  sharedWithCount?: number;
  sharedWithUserGroupsCount?: number;
  lastViewedAt?: Date | string;
  lastChangedAt?: Date;
  owner?: UserInfo;
  assignedClients?: Client[];
  tags?: CollectionTag[];
}

export interface CollectionList {
  items: CollectionListItem[];
  totalCount: number;
}

export type InsightUpdatePayload<TInsightData> = Pick<
  CollectionInsight<TInsightData>,
  'id' | 'data' | 'dataType' | 'viewKey'
>;

export type CreateTemplateData = {
  collectionId: string;
  name: string;
  description?: string | null;
  tags?: CollectionTag[];
  updatedInsights?: InsightUpdatePayload<unknown>[];
  insightsToDelete?: string[];
};

export enum CollectionType {
  Collection = 'collection',
  Template = 'template',
}

export interface SelectedCollectionOption {
  id?: string;
  name: string;
}

export enum CollectionsErrorType {
  NO_ERROR = 'NO_ERROR',
  UNABLE_TO_SAVE = 'UNABLE_TO_SAVE',
  FAILED_TO_GET = 'FAILED_TO_GET',
}

export enum CollectionsPageTab {
  Collections = 'Collections',
  Templates = 'Templates',
}

export type CollectionDataType = 'static' | 'live';

export enum Proposition {
  Consumer = 'consumer',
  Link = 'link',
  Market = 'market',
  DistributionPanel = 'dp',
  Predict = 'predict',
  Retail = 'retail',
  Platform = 'platform',
  Playground = 'playground',
}

export type CreateInsight<TInsightData> = Omit<CollectionInsight<TInsightData>, 'id' | 'createdAt'>;

export type CreateInsightDataFn<TInsightData> = (dataType?: CollectionDataType) => TInsightData;

export type GetTrackingTagsFn = () => Partial<PageTags & MarketTags>;

export enum AddInsightToCollectionsActionType {
  Button = 'Button',
  MenuItem = 'MenuItem',
}

export interface AddInsightToCollectionsActionProps<TInsightData, TViewState extends object> {
  suggestedDescription?: string;
  proposition: Proposition;
  type: string;
  selectDataType?: boolean;
  defaultDataType?: CollectionDataType;
  createInsightDataFn?: CreateInsightDataFn<TInsightData>;
  getTrackingTags?: GetTrackingTagsFn;
  createViewStateFn?: CreateViewStateFn<TViewState>;
  actionType: AddInsightToCollectionsActionType;
  disabled?: boolean;
}

export interface AddInsightToCollectionsModalProps<TInsightData, TViewState extends object> {
  insightInfo: InsightInfo;
  description?: string;
  insightData?: TInsightData;
  onSuccess?: () => void;
  onClose: () => void;
  onError?: (error: Error) => void;
  isOpen: boolean;
  type: string;
  selectDataType: boolean;
  dataType: CollectionDataType;
  setDataType: (dataType: CollectionDataType) => void;
  proposition: Proposition;
  getTrackingTags?: GetTrackingTagsFn;
  createViewStateFn?: CreateViewStateFn<TViewState>;
}

export interface InsightTag {
  key: string;
  value: string;
}

export interface InsightInfo {
  tags: InsightTag[];
  trackingTags?: Partial<MarketTags & AccessTags & ChartTags>;
  chartName: string;
  helperText?: string;
}

export interface ExportPngInfo {
  images: PngImage[];
}

export interface ExportTableInfo {
  table: DataTable;
}

export enum ExportDataType {
  PNG = 'png',
  TABLE = 'table',
}

export type ExportDataTypeMap = {
  [ExportDataType.PNG]: PngImage[];
  [ExportDataType.TABLE]: DataTable;
};

/**
 * Represents an exporter for insights, allowing either PNG or table export, but not both.
 */
export type InsightExporter =
  | { exportPng: () => Promise<ExportPngInfo>; exportTable?: never; subtitle?: string }
  | { exportTable: () => Promise<ExportTableInfo>; exportPng?: never; subtitle?: string };

export enum InsightErrorType {
  SUBSCRIPTION_ERROR = 'SUBSCRIPTION_ERROR',
  BFF_ERROR = 'BFF_ERROR',
  RENDER_ERROR = 'RENDER_ERROR',
  OTHER = 'OTHER',
  EXPIRED = 'EXPIRED',
  MULTI_CONFIG_ERROR = 'MULTI_CONFIG_ERROR',
  DATA_NOT_AVAILABLE = 'DATA_NOT_AVAILABLE',
}

export interface InsightErrorFlags {
  isInsightError: boolean;
  isSubscriptionError: boolean;
  isExpirationError: boolean;
  isMultiConfigError: boolean;
  isOtherRenderError: boolean;
}

export interface ErrorInfo {
  errorType: InsightErrorType;
  loggerMessage: string;
  /**
   * @deprecated userMessage is no longer used
   */
  userMessage?: string;
}

export interface InsightRendererProps<TInsightData> {
  insight: CollectionInsight<TInsightData>;
  onLoaded: () => void;
  onExportReady: (exporter: InsightExporter) => void;
  onError: (errorInfo: ErrorInfo) => void;
  onDataChanged: (data: TInsightData) => Promise<void>;
}
export interface ViewStateInfo<TViewState> {
  viewState: TViewState;
  meta?: Pick<DirectLinkMeta, 'pathname' | 'search'>;
}

export interface ConfigItemsInfo {
  configItems: ConfigItem[];
  errorMessage?: string;
}

export interface InsightDataInfo<TInsightData> {
  data: TInsightData;
  dataType?: CollectionDataType;
}

type RawOrPromise<T> = T | Promise<T>;

export interface ConfigItemCallbacks<TInsightData, TViewState extends object = object> {
  /**
   * Should return the filter configuration based on insight data and currently applied filter values
   * @param args.data The stored insight data (black box)
   * @returns A list of possible config items with options and actual values
   */
  getConfigItems: (args: { data: TInsightData; dataType: CollectionDataType }) => RawOrPromise<ConfigItemsInfo>;

  /**
   * Updates config items based on a new value
   * @param args.data The stored insight data (black box)
   * @param args.configItems The list of config items before the change
   * @param args.newValue The new value to apply
   * @returns The adjusted list of config items.
   */
  applyConfigValue: (args: {
    data: TInsightData;
    configItems: ConfigItem[];
    newValue: ConfigItemValue;
  }) => RawOrPromise<ConfigItemsInfo>;

  /**
   * Should return the updated insight data based on the currently applied config values.
   * @param args.data The stored insight data (black box)
   * @param args.configItems The list of config items with options and actual values
   * @returns The updated insight data and optionally updated insight data type
   */
  getInsightData: (args: {
    data: TInsightData;
    configItems: ConfigItem[];
  }) => RawOrPromise<InsightDataInfo<TInsightData>>;
  /**
   * Event handler to request options for a specific config item
   * Should return config items with updated options for the requested key
   *
   * @param args.data The stored insight data (black box)
   * @param args.configItems The list of config items with options and actual values
   * @param args.key The config item key for which options are requested
   * @return The updated config items with new options for the requested key
   */
  onRequestOptions?: (args: {
    data: TInsightData;
    configItems: ConfigItem[];
    key: string;
  }) => RawOrPromise<ConfigItemsInfo>;
  /**
   * Should return the updated view state data for a new saved view based on the current insight data,
   * with option to add pathname and search params.
   * @param args.data The stored insight data (black box)
   * @param args.pageView The existing page view data
   * @returns The updated view state data, and optionally the pathname and search for the new saved view
   */
  getViewStateInfo?: (args: {
    data: TInsightData;
    pageView?: Pick<PageView<TViewState>, 'data'>;
  }) => RawOrPromise<ViewStateInfo<TViewState>>;

  /** Should return filter values of mandatory filters only
   * @param args.data The stored insight data (black box)
   * @param args.configItems The list of config items with options and actual values
   * @returns The updated configItems without optional config values
   */
  clearConfigValues: (args: { data: TInsightData; configItems: ConfigItem[] }) => RawOrPromise<ConfigItemsInfo>;

  /**
   * Should return the new data object based on the previous state of it as well as the new applied configItemValue
   * @param data The existing stored insight data (black box)
   * @param newValue The new config item value applied
   * @returns insight data object with new config item value applied
   */
  applyMultiEditConfigValue?: (args: {
    data: TInsightData;
    newValue: ConfigItemValue;
  }) => InsightDataInfo<TInsightData>;
}

export interface CollectionsInsightRenderer<TInsightData = unknown, TViewState extends object = object> {
  InsightRenderer: React.FC<InsightRendererProps<TInsightData>>;
  getInsightInfo?: GetInsightInfoFn<TInsightData>;
  configItemCallbacks?: ConfigItemCallbacks<TInsightData, TViewState>;
}

export type GetInsightInfoFn<TInsightData> =
  | ((insight: TInsightData) => Promise<InsightInfo>)
  | ((insight: TInsightData) => InsightInfo);

export interface UserInfo {
  userId: string;
  givenName: string;
  familyName: string;
  email: string;
}

export interface UserInfoPermission {
  accessLevel: AccessLevel;
  userId: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}

export interface UserGroupInfoPermission {
  accessLevel: AccessLevel;
  userGroupId: number;
}

export type UpdatePermission = Pick<UserInfoPermission, 'userId' | 'accessLevel'> | UserGroupInfoPermission;

export interface Client {
  clientId: number;
  clientName: string;
}
